import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { hcConfirmar, hcError, hcExito, hcInfo } from "../../../HC/hcSwal";

import "../../../../styles/ConsultaEvolucion.css";

import { ControllerCitaPaciente, ControllerListarUltimoSignosVitales, ActualizarEstadoCita } from "../../../../Service/Service.citas";
import { ControllerListarPacientesCitas } from "../../../../Service/Service.paciente";
import { ControllerListarAntecedentepaciente } from "../../../../Service/Service.antecedente";
import { ListCategoriaExamen, ListExamenCategoriaID, ListTipoEstudio } from "../../../../Service/Service.categoriaExamen";
import { ListCategoriaCertificados } from "../../../../Service/Service.categoriaCertificados";
import { ControllerListarSegurosClinica } from "../../../../Service/Services.seguros";
import { consultaAbierta } from "../../../../utils/consultaAbierta";
import usePermissions from "../../../../hook/usePermissions";
import { createPDFreceta, textoErrorPdf } from "../../../../Service/Service.laboratorioPDF";
import { createPDFpermisos, createPDFpermisosAsistencia } from "../../../../Service/Service.certificadosPDF";
import { CITA_ESTADO_FINALIZADA } from "../../../../utils/citaFinalizadaAlert";

import { SECCIONES, NIVEL, anchorId, evaluarAvance, evaluarFinalizacion, siguientePendiente } from "./secciones";
import { guardarMotivo, guardarEnfermedad, guardarPlan, guardarNotaEvolucion } from "./guardado";
import generarBorradorSOAP from "./generarBorradorSOAP";

import CabeceraPaciente from "./CabeceraPaciente";
import BannerConsulta from "./BannerConsulta";
import RailAvance from "./RailAvance";
import BarraInferior from "./BarraInferior";
import ResumenFinalizar from "./ResumenFinalizar";
import SeccionTexto from "./secciones/SeccionTexto";
import SeccionSignos from "./secciones/SeccionSignos";
import SeccionHallazgos from "./secciones/SeccionHallazgos";
import SeccionDiagnosticos from "./secciones/SeccionDiagnosticos";
import SeccionOrdenes from "./secciones/SeccionOrdenes";
import SeccionColapsable from "./secciones/SeccionColapsable";

import ModalDiagnostico from "../../../HC/modales/ModalDiagnostico";
import ModalRecetaHC from "../../../HC/modales/ModalRecetaHC";
import PickerModal from "../../../HC/PickerModal";
import { IconoTipoOrden, etiquetaTipoOrden } from "../../../HC/HCIconos";
// HC-MODALES: «Orden de examen de imagen» ya migrado a HCModal. El
// Modalimagenes viejo sigue en el repo sin borrar, por si hay que revertir.
import ModalOrdenImagen from "../../../HC/modales/ModalOrdenImagen";
import ModalInterconsulta from "../../../HC/modales/ModalInterconsulta";
import ModalImagenExterna from "../../../HC/modales/ModalImagenExterna";
import ModalPermisoHC from "../../../HC/modales/ModalPermisoHC";
import ModalAsistenciaHC from "../../../HC/modales/ModalAsistenciaHC";
import ModalOrdenLaboratorio from "../../../HC/modales/ModalOrdenLaboratorio";

const CAT_SISTEMAS = 3;
const CAT_FISICO = 4;
const CAT_PERSONAL = 1;
const ID_ANT_ALERGIA = 3; // "enf. alergia" (categoría Personal)

const fechaCorta = (v) => {
  if (!v) return "";
  const d = new Date(v);
  return isNaN(d) ? String(v) : d.toLocaleDateString("es-EC", { day: "2-digit", month: "short" });
};

/* Combina el catálogo de antecedentes (cat 3/4) con lo guardado para la cita. */
function construirFilas(lista) {
  return (lista || []).map((item) => {
    const ap = (item.antecedentes_paciente || [])[0] || {};
    return {
      id_antecedente: item.id_antecedente,
      descripcion: item.descripcion,
      idantecedente_paciente: ap.idantecedente_paciente || null,
      cp: ap.cp === true,
      sp: ap.sp === true,
      observacion: ap.observacion || "",
    };
  });
}

export default function ConsultaEvolucion() {
  const historial = useSelector((s) => s.historialPacienteRedux?.historial);
  const { hasPermission } = usePermissions();
  const puedeGuardarSignos = hasPermission("signos_vitales.create");
  // ORDENES-ANULAR-01: anular una orden revierte sus cargos por cobrar. El
  // médico crea la orden; deshacerla es de admisión/administración.
  const puedeAnularOrden = hasPermission("ordenes.anular");
  // ORDENES-IMPRIMIR-01: imprimir receta y órdenes (DOCTOR, admins, plataforma).
  const puedeImprimir = hasPermission("ordenes.imprimir");

  const [params] = useState(() => new URLSearchParams(window.location.search));
  const idpaciente = params.get("idpaciente");
  const idcita = params.get("idcita");

  const [cargando, setCargando] = useState(true);
  const [cita, setCita] = useState(null);
  const [paciente, setPaciente] = useState({});
  const [citasPrevias, setCitasPrevias] = useState([]);
  const [alergias, setAlergias] = useState([]);
  const [catalogos, setCatalogos] = useState([]);
  // El seguro "PARTICULAR" de la clínica: hay uno por clínica, así que no se
  // puede fijar un id. Sirve para el respaldo de tarifario.
  const [idSeguroParticular, setIdSeguroParticular] = useState(null);

  const [data, setData] = useState({
    motivo: "",
    enfermedad: "",
    enfermedadId: 0,
    plan: "",
    planId: 0,
    evolucion: "",
    evolucionId: 0,
    signos: {},
    diagnosticos: [],
    sistemas: [],
    fisico: [],
    ordenes: [],
    recetas: [],
    certificados: [],
  });

  // --- estado de autoguardado por sección --------------------------------
  const estadosRef = useRef({});
  const [estadosTick, setEstadosTick] = useState(0);
  const registrarEstado = useCallback((id, obj) => {
    const prev = estadosRef.current[id];
    estadosRef.current[id] = obj;
    if (!prev || prev.status !== obj.status || prev.savedAt !== obj.savedAt) {
      setEstadosTick((t) => t + 1);
    }
  }, []);

  const bloqueado = cita ? !consultaAbierta(cita) : true;
  const finalizada = cita ? Number(cita?.estado_cita?.idestado_cita) === CITA_ESTADO_FINALIZADA : false;

  const ctx = useMemo(
    () => ({
      idcita: Number(idcita),
      idpaciente: Number(idpaciente),
      idclinica: cita?.pacientes?.clinicas?.idclinica,
      iddoctor: cita?.doctor?.iddoctor,
    }),
    [idcita, idpaciente, cita]
  );

  // --- carga inicial (todo en paralelo) ---------------------------------
  const cargarCita = useCallback(async () => {
    const ps = await ControllerCitaPaciente(idpaciente, idcita);
    if (!ps || !ps.success) return null;
    const c = ps.data;
    if (!c.doctor && c.doctores) c.doctor = c.doctores;
    setCita(c);
    setData((d) => ({
      ...d,
      motivo: c.motivo || "",
      enfermedad: c.enfermedad_actual_cita?.[0]?.enfermedad_actual || "",
      enfermedadId: c.enfermedad_actual_cita?.[0]?.idenfermedad_actual_cita || 0,
      plan: c.plan_tratamiento?.[0]?.plan_tratamiento || "",
      planId: c.plan_tratamiento?.[0]?.idplan_tratamiento || 0,
      evolucion: c.nota_evolucion?.[0]?.nota_evolucion || "",
      evolucionId: c.nota_evolucion?.[0]?.idnota_evolucion || 0,
      diagnosticos: c.diagnostico_cita_paciente || [],
      recetas: c.receta_cita || [],
      // Los dos tipos de certificado viven en TABLAS DISTINTAS: el permiso en
      // `certificado_orden` y la asistencia en `asistencia_orden`. Antes solo
      // se leía la primera, así que una asistencia recién emitida se guardaba
      // bien pero no aparecía en la lista -- y se leía como "no se guardó".
      certificados: [
        ...(c.certificados_orden || []).map((x) => ({
          ...x,
          __tipo: "Permiso / reposo",
          __numero: x.numero_certificado,
          __detalle: x.dias_descanso ? `${x.dias_descanso} día(s)` : "",
        })),
        ...(c.asistencia_orden || []).map((x) => ({
          ...x,
          __tipo: "Asistencia",
          __numero: x.numero_asisetncia, // el nombre de la columna trae ese typo
          __detalle: [x.hora_desde, x.hora_hasta].filter(Boolean).join(" – "),
        })),
      ],
    }));
    return c;
  }, [idpaciente, idcita]);

  useEffect(() => {
    let vivo = true;
    (async () => {
      setCargando(true);
      const [, pac, sv, sis, fis, aler, cat, cert] = await Promise.all([
        cargarCita(),
        ControllerListarPacientesCitas(idpaciente).catch(() => null),
        ControllerListarUltimoSignosVitales(idcita).catch(() => null),
        ControllerListarAntecedentepaciente(idpaciente, CAT_SISTEMAS, idcita).catch(() => null),
        ControllerListarAntecedentepaciente(idpaciente, CAT_FISICO, idcita).catch(() => null),
        ControllerListarAntecedentepaciente(idpaciente, CAT_PERSONAL, idcita).catch(() => null),
        ListCategoriaExamen().catch(() => null),
        ListCategoriaCertificados().catch(() => null),
      ]);
      if (!vivo) return;

      if (pac?.success && pac.data) {
        setPaciente(pac.data);
        setCitasPrevias((pac.data.citas || []).filter((x) => String(x.idcita) !== String(idcita)));
      }
      const svRow = sv?.success && sv.data?.length ? sv.data[0] : {};
      setData((d) => ({
        ...d,
        signos: {
          temperatura: svRow.temperatura ?? "",
          pulso: svRow.pulso ?? "",
          presion: svRow.presion ?? "",
          frecuencia_respiratoria: svRow.frecuencia_respiratoria ?? "",
          saturacion_oxigeno: svRow.saturacion_oxigeno ?? "",
          peso: svRow.peso ?? "",
          talla: svRow.talla ?? "",
          imc: svRow.imc ?? "",
          fecha: svRow.fecha,
          hora: svRow.hora,
          idsignosvitales: svRow.idsignosvitales,
          __origenRecepcion: Boolean(svRow.idsignosvitales),
        },
        sistemas: construirFilas(sis?.data),
        fisico: construirFilas(fis?.data),
      }));

      // Seguro PARTICULAR de la clínica del paciente, para el respaldo de
      // tarifario. Se busca por nombre porque es lo que el dato soporta.
      const idclinicaPac = pac?.data?.idclinica;
      if (idclinicaPac) {
        const segs = await ControllerListarSegurosClinica(idclinicaPac).catch(() => null);
        const part = (segs?.data || []).find(
          (x) => String(x.nombre || "").trim().toUpperCase() === "PARTICULAR"
        );
        if (vivo && part) setIdSeguroParticular(part.idseguro);
      }

      if (aler?.success) {
        const fila = (aler.data || []).find((x) => x.id_antecedente === ID_ANT_ALERGIA);
        const ap = fila?.antecedentes_paciente?.[0];
        if (ap && (ap.tiene === true || ap.cp === true)) {
          setAlergias(ap.observacion ? [ap.observacion] : ["registrada"]);
        }
      }
      // Las dos familias de catálogo usan PK distinta: los exámenes
      // `IdCategoriaExamen` y los certificados `IdCategoriaCertificado`. Se
      // normaliza a `__key` aquí. Sin esto, las dos tarjetas de certificado
      // compartían la clave "undefined" y elegir «Asistencia» abría siempre
      // «Permiso», que es lo que se reportó.
      const cats = [
        ...(cat?.data || []).map((x) => ({ ...x, __key: `ex-${x.IdCategoriaExamen}` })),
        ...(cert?.data || []).map((x) => ({
          ...x,
          __cert: true,
          __key: `ce-${x.IdCategoriaCertificado}`,
        })),
      ];
      setCatalogos(cats);
      setCargando(false);
    })();
    return () => {
      vivo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idpaciente, idcita, historial]);

  // --- scrollspy: sección visible --------------------------------------
  const [seccionActual, setSeccionActual] = useState(SECCIONES[0].id);
  useEffect(() => {
    if (cargando) return;
    const obs = new IntersectionObserver(
      (entries) => {
        const vis = entries.filter((e) => e.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (vis[0]) {
          const id = vis[0].target.id.replace("cev-sec-", "");
          setSeccionActual(id);
        }
      },
      // el descuento superior es el alto del bloque fijo (cabecera + franja)
      { rootMargin: "-125px 0px -50% 0px", threshold: [0.1, 0.5] }
    );
    SECCIONES.forEach((s) => {
      const el = document.getElementById(anchorId(s.id));
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, [cargando]);

  const navegar = useCallback((id) => {
    const el = document.getElementById(anchorId(id));
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      setSeccionActual(id);
      const foco = el.querySelector("textarea, input, button");
      if (foco) setTimeout(() => foco.focus({ preventScroll: true }), 350);
    }
  }, []);

  // --- avance / finalización ------------------------------------------
  const avance = useMemo(() => evaluarAvance(data), [data]);
  const finalizacion = useMemo(() => evaluarFinalizacion(avance), [avance]);
  const pendienteNav = useMemo(() => siguientePendiente(avance, seccionActual), [avance, seccionActual]);

  const estadoGlobal = useMemo(() => {
    const vals = Object.values(estadosRef.current);
    if (vals.some((v) => v?.status === "error")) return "error";
    if (vals.some((v) => v?.status === "saving" || v?.status === "dirty")) return "saving";
    return "saved";
  }, [estadosTick]); // eslint-disable-line react-hooks/exhaustive-deps
  const ultimoGuardado = useMemo(() => {
    const fechas = Object.values(estadosRef.current).map((v) => v?.savedAt).filter(Boolean);
    return fechas.length ? new Date(Math.max(...fechas.map((f) => +f))) : null;
  }, [estadosTick]); // eslint-disable-line react-hooks/exhaustive-deps

  const flushTodo = useCallback(async () => {
    await Promise.all(Object.values(estadosRef.current).map((v) => (v?.flush ? v.flush() : null)));
  }, []);

  // --- modales existentes (mismo flujo que IngresoConsulta) -----------
  const [modales, setModales] = useState({});
  const [tipoEstudios, setTipoEstudios] = useState([]);
  const [examenPorCategoria, setExamenPorCategoria] = useState([]);
  const [idCategoriaExamen, setIdCategoriaExamen] = useState();
  const cerrarModales = () => setModales({});

  const abrirFormularioCategoria = useCallback(
    async (form) => {
      if (!form) return;
      const [resExam, resTipo] = await Promise.all([
        ListExamenCategoriaID(form.IdCategoriaExamen).catch(() => ({ success: false })),
        ListTipoEstudio(form.IdCategoriaExamen).catch(() => ({ success: false })),
      ]);
      setIdCategoriaExamen(form.IdCategoriaExamen);
      if (resExam.success) {
        let datos = resExam.data;
        const idSeguro = cita?.pacientes?.seguros?.idseguro;
        // TARIFARIO-RESPALDO-PARTICULAR-01: el estudio se hace igual, lo cubra
        // el plan o no; si no está en el tarifario del seguro se cobra a tarifa
        // PARTICULAR (el backend resuelve el precio con ese mismo respaldo).
        // Por eso la lista visible incluye lo del seguro del paciente Y lo que
        // tenga tarifa particular: antes filtraba solo por su seguro y dejaba
        // fuera exámenes que sí se pueden pedir.
        datos = datos.filter((ex) =>
          (ex.procedimiento_company_precios || []).some(
            (p) => p.seguro_id === idSeguro || (idSeguroParticular && p.seguro_id === idSeguroParticular)
          )
        );
        setExamenPorCategoria(datos);
      }
      if (resTipo.success) {
        setTipoEstudios(resTipo.data.map((it) => ({ ...it, disabled: false, checked: false })));
      }
      const map = {
        LABORATORIO: "laboratorio",
        IMAGENES: "imagenes",
        INTERCONSULTA: "interconsulta",
        "IMAGENES EXTERNAS": "imagenesExternas",
        PERMISOS: "permiso",
        ASISTENCIA: "asistencia",
      };
      const key = map[form.Tipo];
      if (key) setModales({ [key]: true });
    },
    [cita, idSeguroParticular]
  );

  const refrescarTrasModal = useCallback(async () => {
    cerrarModales();
    await cargarCita();
  }, [cargarCita]);

  // --- generar borrador SOAP -----------------------------------------
  const generarBorrador = useCallback(async () => {
    const borrador = generarBorradorSOAP(data);
    if (!borrador) {
      hcInfo("Aún no hay nada que resumir", "Escribe primero en las otras secciones.");
      return;
    }
    if (data.evolucion && data.evolucion.trim()) {
      const r = await hcConfirmar({
        titulo: "Ya hay texto en la nota",
        texto: "El borrador reemplazará lo que hay escrito. Puedes seguir editándolo después.",
        confirmar: "Reemplazar",
        cancelar: "Dejarlo como está",
      });
      if (!r.isConfirmed) return;
    }
    setData((d) => ({ ...d, evolucion: borrador }));
  }, [data]);

  // --- finalizar -----------------------------------------------------
  const [resumenAbierto, setResumenAbierto] = useState(false);
  const [finalizando, setFinalizando] = useState(false);

  const intentarFinalizar = async () => {
    if (!finalizacion.puedeFinalizar) return;
    await flushTodo();
    setResumenAbierto(true);
  };
  const confirmarFinalizar = async () => {
    setFinalizando(true);
    const r = await ActualizarEstadoCita({ idcita: Number(idcita), estado: CITA_ESTADO_FINALIZADA });
    setFinalizando(false);
    if (r && r.success) {
      hcExito("Atención finalizada");
      setResumenAbierto(false);
      await cargarCita();
    } else {
      hcError("No se pudo finalizar", r?.message || "Intenta de nuevo.");
    }
  };
  // --- impresión (mismos generadores que usaban TableExamen/TableCertificados) ---
  const avisoPdf = (r) => {
    if (r && r.success === false) {
      hcError("No se pudo generar el PDF", textoErrorPdf(r));
    }
  };
  const imprimirReceta = async () => {
    // createPDFreceta es por CITA, no por receta: imprime la receta completa.
    avisoPdf(await createPDFreceta(idcita, paciente?.nombres_completos || "Paciente"));
  };
  const imprimirCertificado = async (c) => {
    avisoPdf(
      c.__tipo === "Asistencia"
        ? await createPDFpermisosAsistencia(c.idasistencia_orden)
        : await createPDFpermisos(c.idcertificado_orden)
    );
  };

  const [iniciando, setIniciando] = useState(false);
  const iniciarConsulta = async () => {
    setIniciando(true);
    const r = await ActualizarEstadoCita({ idcita: Number(idcita), estado: 5 });
    setIniciando(false);
    if (r && r.success) {
      await cargarCita();
    } else {
      hcError("No se pudo iniciar la consulta", r?.message || "Revisa el estado de la cita.");
    }
  };

  const guardarYSalir = async () => {
    await flushTodo();
    window.close();
    setTimeout(() => {
      window.location.href = `/paciente_historial?idpaciente=${idpaciente}`;
    }, 150);
  };

  // --- atajos de teclado -------------------------------------------
  useEffect(() => {
    const onKey = (e) => {
      if (!e.altKey) return;
      const idx = SECCIONES.findIndex((s) => s.id === seccionActual);
      if (e.key === "ArrowDown" && SECCIONES[idx + 1]) {
        e.preventDefault();
        navegar(SECCIONES[idx + 1].id);
      } else if (e.key === "ArrowUp" && SECCIONES[idx - 1]) {
        e.preventDefault();
        navegar(SECCIONES[idx - 1].id);
      } else if ((e.key === "f" || e.key === "F") && pendienteNav) {
        e.preventDefault();
        navegar(pendienteNav);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [seccionActual, pendienteNav, navegar]);

  const set = (k) => (v) => setData((d) => ({ ...d, [k]: v }));
  const amberMsg = (id) => {
    const av = avance[id];
    if (!av || av.estado === "completa") return null;
    if (id === "sistemas" || id === "fisico") return `Faltan ${av.detalle} por evaluar`;
    return "Falta completar esta sección";
  };
  const esAmber = (id) => {
    const sec = SECCIONES.find((s) => s.id === id);
    return sec && sec.nivel !== NIVEL.OPCIONAL && ["vacia", "parcial"].includes(avance[id]?.estado);
  };

  if (!idpaciente || !idcita) {
    return <div className="cev__empty">Falta el paciente o la cita en la dirección.</div>;
  }
  if (cargando) {
    return (
      <div className="cev">
        <div className="cev__skel" style={{ height: 62 }} />
        <div className="cev__body">
          <div className="cev__skel" style={{ height: 420 }} />
          <div className="cev__col">
            {[220, 180, 260, 200].map((h, i) => (
              <div key={i} className="cev__skel" style={{ height: h }} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  const fechaPrevia = citasPrevias[0] ? fechaCorta(citasPrevias[0].fecha_inicio) : null;
  const verAnterior = () => {
    if (citasPrevias[0]) window.open(`/paciente_historial?idpaciente=${idpaciente}&idcita=${citasPrevias[0].idcita}`, "_blank");
  };

  return (
    <div className="cev">
      {/* Cabecera + franja de avance viajan juntas y quedan fijas arriba
          durante todo el scroll: la franja ES la navegación entre secciones.
          El rail lateral sigue existiendo como variante de RailAvance
          (variant="lateral") por si se quiere volver a él, pero el ancho
          completo para el contenido ganó. */}
      <div className="cev__top">
        <CabeceraPaciente
          paciente={paciente}
          cita={cita}
          alergias={alergias}
          onConsultasAnteriores={citasPrevias.length ? () => window.open(`/paciente_historial?idpaciente=${idpaciente}`, "_blank") : undefined}
        />
        <RailAvance avance={avance} seccionActual={seccionActual} onNavegar={navegar} variant="horizontal" />
      </div>

      <div className="cev__body cev__body--full">
        <div className="cev__col">
          <BannerConsulta cita={cita} onIniciar={iniciarConsulta} iniciando={iniciando} />

          {/* Orden clínico (el del submenú original). El nivel de exigencia no
              ordena nada: lo dice el chip de cada cabecera y el marcador de su
              pastilla en la franja. Este orden debe coincidir con SECCIONES,
              que es de donde salen «‹ anterior / siguiente ›» y Alt+↑/↓. */}

          {/* 1 · Motivo de consulta (era la sección CONSULTA) */}
          <SeccionTexto
            seccionId="motivo"
            valor={data.motivo}
            onChangeValor={set("motivo")}
            guardar={(t) => guardarMotivo(ctx, t)}
            bloqueado={bloqueado}
            rows={2}
            placeholder="Motivo de la consulta…"
            quickChips={["Control", "Primera vez", "Ficha médica", "Chequeo empresarial"]}
            registrarEstado={registrarEstado}
            onNavegar={navegar}
            amberFoot={esAmber("motivo")}
            amberMsg={amberMsg("motivo")}
          />

          {/* 2 · Signos vitales */}
          <SeccionSignos
            seccionId="signos"
            signos={data.signos}
            onChangeSignos={set("signos")}
            ctx={ctx}
            bloqueado={bloqueado}
            puedeGuardar={puedeGuardarSignos}
            registrarEstado={registrarEstado}
            onNavegar={navegar}
            amberFoot={esAmber("signos")}
            amberMsg={amberMsg("signos")}
          />

          {/* 3 · Enfermedad actual */}
          <SeccionTexto
            seccionId="enfermedad"
            valor={data.enfermedad}
            onChangeValor={set("enfermedad")}
            guardar={(t) => guardarEnfermedad(ctx, t, data.enfermedadId)}
            onGuardado={(res) => res?.idenfermedad_actual_cita && setData((d) => ({ ...d, enfermedadId: res.idenfermedad_actual_cita }))}
            bloqueado={bloqueado}
            placeholder="Historia de la enfermedad actual…"
            registrarEstado={registrarEstado}
            onNavegar={navegar}
            amberFoot={esAmber("enfermedad")}
            amberMsg={amberMsg("enfermedad")}
          />

          {/* 4 · Revisión por sistemas (venía de Antecedentes) */}
          <SeccionHallazgos
            seccionId="sistemas"
            filas={data.sistemas}
            onCambiarFilas={set("sistemas")}
            ctx={ctx}
            bloqueado={bloqueado}
            registrarEstado={registrarEstado}
            onNavegar={navegar}
            onVerAnterior={verAnterior}
            fechaAnterior={fechaPrevia}
            amberFoot={esAmber("sistemas")}
            amberMsg={amberMsg("sistemas")}
          />

          {/* 5 · Examen físico (venía de Antecedentes) */}
          <SeccionHallazgos
            seccionId="fisico"
            filas={data.fisico}
            onCambiarFilas={set("fisico")}
            ctx={ctx}
            bloqueado={bloqueado}
            registrarEstado={registrarEstado}
            onNavegar={navegar}
            onVerAnterior={verAnterior}
            fechaAnterior={fechaPrevia}
            amberFoot={esAmber("fisico")}
            amberMsg={amberMsg("fisico")}
          />

          {/* 6 · Diagnósticos */}
          <SeccionDiagnosticos
            diagnosticos={data.diagnosticos}
            bloqueado={bloqueado}
            onAbrirModal={() => setModales({ diagnostico: true })}
            onRefrescar={cargarCita}
            onNavegar={navegar}
            amberFoot={esAmber("diagnosticos")}
            amberMsg={amberMsg("diagnosticos")}
          />

          {/* 7 · Plan de tratamiento */}
          <SeccionTexto
            seccionId="plan"
            valor={data.plan}
            onChangeValor={set("plan")}
            guardar={(t) => guardarPlan(ctx, t, data.planId)}
            onGuardado={(res) => res?.idplan_tratamiento && setData((d) => ({ ...d, planId: res.idplan_tratamiento }))}
            bloqueado={bloqueado}
            rows={9}
            placeholder="Plan de tratamiento…"
            registrarEstado={registrarEstado}
            onNavegar={navegar}
            amberFoot={esAmber("plan")}
            amberMsg={amberMsg("plan")}
          />

          {/* 8 · Nota de evolución */}
          <SeccionTexto
            seccionId="evolucion"
            valor={data.evolucion}
            onChangeValor={set("evolucion")}
            guardar={(t) => guardarNotaEvolucion(ctx, t, data.evolucionId)}
            onGuardado={(res) => res?.idnota_evolucion && setData((d) => ({ ...d, evolucionId: res.idnota_evolucion }))}
            bloqueado={bloqueado}
            rows={10}
            placeholder="Nota de evolución…"
            registrarEstado={registrarEstado}
            onNavegar={navegar}
            amberFoot={esAmber("evolucion")}
            amberMsg={amberMsg("evolucion")}
            headerExtra={
              <button type="button" className="cev__btn cev__btn--sm" disabled={bloqueado} onClick={generarBorrador}>
                Generar borrador
              </button>
            }
          />

          {/* 9 · Recetas */}
          <SeccionColapsable
            seccionId="recetas"
            items={data.recetas || []}
            columnas={[
              { key: "medicamentos", label: "Medicación" },
              { key: "posologia", label: "Posología" },
              { key: "indicaciones", label: "Indicaciones" },
            ]}
            render={(r) => (
              <>
                <td>{r.medicamentos}</td>
                <td>{r.posologia}</td>
                <td>{r.indicaciones}</td>
              </>
            )}
            textoAgregar="+ Agregar receta"
            bloqueado={bloqueado}
            onAgregar={() => setModales({ receta: true })}
            accionCabecera={puedeImprimir ? { label: "Imprimir receta", onClick: imprimirReceta } : undefined}
            onNavegar={navegar}
          />

          {/* 10 · Órdenes (era EXAMENES) */}
          <SeccionOrdenes
            cita={cita}
            catalogos={catalogos}
            bloqueado={bloqueado}
            puedeAnular={puedeAnularOrden}
            puedeImprimir={puedeImprimir}
            onAbrirFormulario={abrirFormularioCategoria}
            onRefrescar={cargarCita}
            onNavegar={navegar}
          />

          {/* 11 · Certificados */}
          <SeccionColapsable
            seccionId="certificados"
            items={data.certificados || []}
            columnas={[
              { key: "tipo", label: "Tipo" },
              { key: "numero", label: "N.º" },
              { key: "detalle", label: "Detalle" },
              { key: "fecha", label: "Fecha" },
            ]}
            render={(c) => (
              <>
                <td>{c.__tipo}</td>
                <td>{c.__numero || "—"}</td>
                <td>{c.__detalle || "—"}</td>
                <td>{c.fecha || fechaCorta(c.fecha_desde)}</td>
              </>
            )}
            textoAgregar="+ Emitir certificado"
            bloqueado={bloqueado}
            onAgregar={() => setModales({ certificadoMenu: true })}
            onAccion={imprimirCertificado}
            textoAccion="Imprimir"
            onNavegar={navegar}
          />
        </div>
      </div>

      <BarraInferior
        estadoGlobal={estadoGlobal}
        ultimoGuardado={ultimoGuardado}
        finalizacion={finalizacion}
        hayPendientesNav={Boolean(pendienteNav)}
        onIrAloQueFalta={() => pendienteNav && navegar(pendienteNav)}
        onGuardarSalir={guardarYSalir}
        onFinalizar={intentarFinalizar}
        finalizarDisponible={!finalizada}
      />

      {resumenAbierto && (
        <ResumenFinalizar
          bloqueos={finalizacion.bloqueos}
          avisos={finalizacion.avisos}
          confirmando={finalizando}
          onCancelar={() => setResumenAbierto(false)}
          onConfirmar={confirmarFinalizar}
        />
      )}

      {/* ---------- modales reutilizados ---------- */}
      <ModalDiagnostico
        show={!!modales.diagnostico}
        onHide={cerrarModales}
        cita={cita}
        paciente={paciente}
        onGuardado={cargarCita}
      />
      <ModalRecetaHC
        show={!!modales.receta}
        onHide={cerrarModales}
        cita={cita}
        paciente={paciente}
        onGuardado={cargarCita}
      />

      {/* Antes era una lista de <button> sin estilo. Ahora tarjetas con icono. */}
      <PickerModal
        isOpen={!!modales.certificadoMenu}
        title="Emitir certificado"
        subtitle="Elige el tipo; después completas el formulario."
        options={catalogos
          .filter((c) => c.__cert)
          .map((c) => ({
            key: c.__key,
            titulo: etiquetaTipoOrden(c.Tipo),
            descripcion:
              String(c.Tipo).toUpperCase() === "PERMISOS"
                ? "Reposo laboral con días de descanso"
                : "Constancia de que el paciente asistió a la consulta",
            icono: <IconoTipoOrden tipo={c.Tipo} />,
          }))}
        onSelect={(key) => {
          const cat = catalogos.find((c) => c.__cert && c.__key === key);
          if (cat) abrirFormularioCategoria(cat);
        }}
        onClose={cerrarModales}
      />

      <ModalOrdenImagen show={!!modales.imagenes} onHide={cerrarModales} tiposEstudio={tipoEstudios} examenes={examenPorCategoria} cita={cita} paciente={paciente} ListaCitaPaciente={cargarCita} IdCategoriaExamen={idCategoriaExamen} onSaveSuccess={refrescarTrasModal} />
      <ModalOrdenLaboratorio show={!!modales.laboratorio} onHide={cerrarModales} tiposEstudio={tipoEstudios} examenes={examenPorCategoria} cita={cita} paciente={paciente} ListaCitaPaciente={cargarCita} onSaveSuccess={refrescarTrasModal} />
      <ModalInterconsulta show={!!modales.interconsulta} onHide={cerrarModales} cita={cita} paciente={paciente} ListaCitaPaciente={cargarCita} IdCategoriaExamen={idCategoriaExamen} onSaveSuccess={refrescarTrasModal} />
      <ModalImagenExterna show={!!modales.imagenesExternas} onHide={cerrarModales} cita={cita} paciente={paciente} ListaCitaPaciente={cargarCita} IdCategoriaExamen={idCategoriaExamen} onSaveSuccess={refrescarTrasModal} />
      <ModalPermisoHC show={!!modales.permiso} onHide={cerrarModales} cita={cita} paciente={paciente} ListaCitaPaciente={cargarCita} IdCategoriaExamen={idCategoriaExamen} onSaveSuccess={refrescarTrasModal} />
      <ModalAsistenciaHC show={!!modales.asistencia} onHide={cerrarModales} cita={cita} paciente={paciente} ListaCitaPaciente={cargarCita} IdCategoriaExamen={idCategoriaExamen} onSaveSuccess={refrescarTrasModal} />
    </div>
  );
}
